import { NestedTypedEventEmitter3 } from './event-emitter';
import { LogEvent } from './log';
import {
  MESSAGE_HEADER_LEN_BYTES,
  MessagePayloadType,
  MessageStruct,
  parseMessageStruct,
  getMessageJsonPayload,
} from './message';
import {
  getDataViewOfTypedArray,
  Maybe,
  NoArgsVoidFunc,
  noopFunc,
  Nullable,
  perf,
} from './utils';
import { StreamReader } from './stream-reader';
import { VideoCodecInfo, AudioCodecInfo } from './av';

export type TransportEvents = {
  ready: NoArgsVoidFunc;
  closed: NoArgsVoidFunc;
  error: (err: Error, innerErr?: Error) => void;
  message: (
    msgData: MessageStruct,
    groupId: number,
    tag: string,
    streamIdx: number
  ) => void;
  streamAdded: (streamReader: StreamReader) => void;
  streamRemoved: (streamReader: StreamReader) => void;
};

export interface TransportStreamStatus {
  tag: string;
  streamIndex: number;
  timeSinceStart: number;
  timeSinceLastData: number;
  timeSinceLastMsg: number;
  bytesReadCount: number;
  msgCount: number;
  avgKbps: number;
  meanMsgDeltaMs: number;
  timeoutMs: number;
}

export interface TransportStatusInfo {
  streamReaders: Array<TransportStreamStatus>;
  activeStreamReaders: Array<TransportStreamStatus>;
}

const DEFAULT_INIT_READ_TIMEOUT_MS = 2000;
const MIN_READ_TIMEOUT_MS = 160;
const READ_TIMEOUT_FACTOR = 5;

type StreamReaderMap = { [streamIdx: number]: StreamReader };

export class Transport extends NestedTypedEventEmitter3<
  TransportEvents & LogEvent
> {
  private _transportConnection: Nullable<WebTransport> = null;
  private _nextStreamIdx = 0;
  private _streamReaders: StreamReaderMap = {};
  private _groupIdToStreamReader: Map<number, StreamReader> = new Map();
  private _videoCodecInfo: VideoCodecInfo = {};
  private _audioCodecInfo: AudioCodecInfo = {};

  constructor() {
    super();
  }

  isConnectionReady(): boolean {
    return !!this._transportConnection;
  }

  async disconnect() {
    if (this._transportConnection) {
      await this._transportConnection.close();
      this._transportConnection = null;
      this.emit('closed');
    }
  }

  async connect(serverUrl: string) {
    if (this._transportConnection) {
      throw new Error('Transport already connected');
    }

    let transportConnection: WebTransport;
    try {
      transportConnection = new WebTransport(serverUrl);
      this.emit('log', `Transport connecting to ${serverUrl}`);
    } catch (err) {
      this.emit('error', new Error(`Failed to create connection: ${err}`), err as Error);
      return;
    }

    this.emit('log', 'Transport created, waiting for connection to be ready');
    try {
      await transportConnection.ready;
      this._transportConnection = transportConnection;
    } catch (err) {
      this.emit('error', new Error(`Failed to connect: ${err}`), err as Error);
      return;
    }
    this.emit('log', 'Transport connection ready');
    this.emit('ready');

    transportConnection.closed
      .catch((err) => {
        this.emit('error', new Error(`Exception on closing WT connection: ${err}`), err);
      })
      .finally(() => {
        this.emit('closed');
      });
  }

  async accept() {
    if (!this._transportConnection) {
      throw new Error('Transport not connected');
    }

    const incomingStreamReader: ReadableStreamDefaultReader<WebTransportBidirectionalStream> =
      this._transportConnection.incomingBidirectionalStreams.getReader();

    while (true) {
      this.emit('log', 'Waiting to accept new incoming transport streams');

      let incomingStream: Maybe<WebTransportBidirectionalStream>;
      let incomingStreamDone: boolean = false;

      try {
        ({ value: incomingStream, done: incomingStreamDone } = await incomingStreamReader.read());
      } catch (err) {
        this.emit('error', new Error(`Exception on incoming stream read: ${err}`), err as Error);
        return;
      }

      if (incomingStream) {
        await this._addIncomingStream(incomingStream);
      }

      if (incomingStreamDone) {
        this.emit('log', 'Incoming stream reader done');
        break;
      }
    }
  }

  getConnectionStats(): Promise<WebTransportStats> {
    return Promise.resolve({} as WebTransportStats);
  }

  getStatusInfo(): TransportStatusInfo {
    return {
      streamReaders: [],
      activeStreamReaders: [],
    };
  }

  getStats() {
    return {};
  }

  private async _addIncomingStream(incomingStream: WebTransportBidirectionalStream) {
    const streamIdx = this._nextStreamIdx++;
    const streamReader = new StreamReader(streamIdx, incomingStream);
    this._streamReaders[streamIdx] = streamReader;

    streamReader.onMessage = (msg, groupId, tag) => {
      this.emit('message', msg, groupId, tag, streamIdx);
    };

    streamReader.onDescription = (segmentIndex, description) => {
      this._groupIdToStreamReader.set(description.GroupId, streamReader);
      this.emit('streamAdded', streamReader);
    };

    streamReader.onVideoCodecInfo = (codecInfo) => {
      this._videoCodecInfo = codecInfo;
    };

    streamReader.onAudioCodecInfo = (codecInfo) => {
      this._audioCodecInfo = codecInfo;
    };

    streamReader.onClose = () => {
      delete this._streamReaders[streamIdx];
      if (streamReader.groupId >= 0) {
        this._groupIdToStreamReader.delete(streamReader.groupId);
      }
      this.emit('streamRemoved', streamReader);
    };

    streamReader.consume();
  }

  getStreamReaderByGroupId(groupId: number): StreamReader | undefined {
    return this._groupIdToStreamReader.get(groupId);
  }

  getVideoCodecInfo() {
    return this._videoCodecInfo;
  }

  getAudioCodecInfo() {
    return this._audioCodecInfo;
  }
}
